import {SpecificImprovementScoreItem} from "../climate-advice"
import React, {useState} from "react"
import {ImprovementScoreItem} from "./improvement-score-item"
import {ImprovementScore} from "./improvement-score"
import {FuelType} from "../../../domain/fuel-type"
import {invalidValidationResult, ValidationResult} from "../../../util/validation/validation-result"
import {toFormattedNumber} from "../../../components-shared/formatted-number/formatted-number"
import {useServices} from "../../../services/services"
import {FormImprovementFuelInput, FormImprovementInputProps, FormImprovementNumberInput, NumericValueAndValidationResult} from "./form-improvement-input"
import {ImprovementInputParameters} from "../../../services/api/improvements-api"
import {WithVerticalSpacing} from "../../../components-shared/spacing/with-vertical-spacing"
import {HeadingSmall} from "../../../components-shared/heading/heading"
import {formatAmount, formatImpact} from "../../dashboard/transactions/transaction-amount/transaction-amount"
import {getDurationFromYears} from "../../../util/date-utils"
import {ComputeGenericValue, ComputeNumericValue, getInitialComputeGenericValue, getInitialComputeNumericValue} from "./compute-value"
import {validatePositiveNumber} from "./validation"

export interface CommuteByBikeImprovementScore extends ImprovementScore {
  currentDaysPerWeek: number
  desiredDaysPerWeek: number
  distanceToWork: number
  fuelType: FuelType
  kmPerLiter: number
  buyVehicleCost: number
  vacationWeeks: number
  kmReductionPerYear: number
}

export class CommuteByBikeImprovementScoreItem implements ImprovementScoreItem {
  constructor(public improvementScore: CommuteByBikeImprovementScore) {}

  get title(): string { return "Fietsen naar je werk" }
  get explanation(): string {
    const reducedDaysPerWeek = this.improvementScore.currentDaysPerWeek - this.improvementScore.desiredDaysPerWeek
    return `Gebaseerd op ${reducedDaysPerWeek} ${reducedDaysPerWeek === 1 ? "dag" : "dagen"} per week met de fiets, met een afstand van ${this.improvementScore.distanceToWork} km tot je werk`
  }
}

export interface CommuteByBikeFormProps {
  improvementScoreItem: CommuteByBikeImprovementScoreItem
  updateImprovementScoreItem: (newImprovementScoreItem: SpecificImprovementScoreItem) => void
}

export const CommuteByBikeForm: React.FC<CommuteByBikeFormProps> = ({improvementScoreItem, updateImprovementScoreItem}) => {
  const services = useServices()
  const [viewModel, setViewModel] = useState<CommuteByBikeFormViewModel>(getInitialCommuteByBikeFormViewModel(improvementScoreItem))
  const controller = new AbortController()

  const handleNumberInputChange = (field: keyof CommuteByBikeValues) => async (numericValueAndValidationResult: NumericValueAndValidationResult) => {
    const updatedViewModel = {...viewModel}
    updatedViewModel.values[field].value = numericValueAndValidationResult.value
    if (numericValueAndValidationResult.resultType === "numeric") {
      (updatedViewModel.values[field] as ComputeNumericValue).numericValue = numericValueAndValidationResult.numericValue
    }
    updatedViewModel.values[field].partialValidationResult = numericValueAndValidationResult.partialValidationResult

    validateViewModel(updatedViewModel)
    setViewModel(updatedViewModel)

    if (updatedViewModel.isValid) {
      if (services === null) return

      const values = getValuesFromViewModel(updatedViewModel)

      const improvementScore = await services.improvementsApi.getImprovementScore(improvementScoreItem.improvementScore.improvement, values, controller.signal)
      updateImprovementScoreItem(new CommuteByBikeImprovementScoreItem(improvementScore as CommuteByBikeImprovementScore))
    }
  }

  const formImprovementInputProps = (key: keyof CommuteByBikeValues): FormImprovementInputProps => {
    return {
      description: viewModel.values[key].description,
      value: viewModel.values[key].value,
      onChange: handleNumberInputChange(key),
      validationResult: viewModel.values[key].validationResult
    }
  }

  return <div>
    <WithVerticalSpacing>
      <FormImprovementNumberInput {...formImprovementInputProps("currentDaysPerWeek")}/>
      <FormImprovementNumberInput {...formImprovementInputProps("desiredDaysPerWeek")}/>
      <FormImprovementNumberInput {...formImprovementInputProps("distanceToWork")}/>
      <FormImprovementNumberInput {...formImprovementInputProps("kmPerLiter")}/>
      <FormImprovementNumberInput {...formImprovementInputProps("vacationWeeks")}/>
      <FormImprovementNumberInput {...formImprovementInputProps("buyVehicleCost")}/>
      <FormImprovementFuelInput {...formImprovementInputProps("fuelType")}/>

      <div>
        <HeadingSmall>Berekend resultaat</HeadingSmall>
        <table>
          <tbody>
            <tr>
              <td>Aantal dagen per week minder met de auto</td>
              <td>{toFormattedNumber(improvementScoreItem.improvementScore.currentDaysPerWeek - improvementScoreItem.improvementScore.desiredDaysPerWeek, 0)}</td>
            </tr>
            <tr>
              <td>Kilometers minder autorijden per jaar</td>
              <td>{toFormattedNumber(improvementScoreItem.improvementScore.kmReductionPerYear, 0)}</td>
            </tr>
            <tr>
              <td>CO<sub>2</sub>-reductie per jaar</td>
              <td>{toFormattedNumber(improvementScoreItem.improvementScore.co2ReductionPerYear, 0)} kg</td>
            </tr>
            {improvementScoreItem.improvementScore.financialInvestment > 0 && <tr>
              <td>Initiële investering</td>
              <td>{formatAmount(improvementScoreItem.improvementScore.financialInvestment, 0)} ({formatImpact(improvementScoreItem.improvementScore.co2Investment, 0)} CO<sub>2</sub>)</td>
            </tr>}
            {improvementScoreItem.improvementScore.financialPaybackPeriod !== null && (improvementScoreItem.improvementScore.financialPaybackPeriod > 0) && <tr>
              <td>Terugverdientijd (financieel)</td>
              <td>{getDurationFromYears(improvementScoreItem.improvementScore.financialPaybackPeriod)}</td>
            </tr>}
            {improvementScoreItem.improvementScore.co2PaybackPeriod !== null && (improvementScoreItem.improvementScore.co2PaybackPeriod > 0) && <tr>
              <td>Terugverdientijd (CO<sub>2</sub>)</td>
              <td>{getDurationFromYears(improvementScoreItem.improvementScore.co2PaybackPeriod)}</td>
            </tr>}
          </tbody>
        </table>
      </div>
    </WithVerticalSpacing>
  </div>
}

function getValuesFromViewModel(updatedViewModel: { isValid: boolean; values: CommuteByBikeValues }) {
  const values: ImprovementInputParameters = {

    currentDaysPerWeek: updatedViewModel.values.currentDaysPerWeek.numericValue,
    desiredDaysPerWeek: updatedViewModel.values.desiredDaysPerWeek.numericValue,
    distanceToWork: updatedViewModel.values.distanceToWork.numericValue,
    kmPerLiter: updatedViewModel.values.kmPerLiter.numericValue,
    buyVehicleCost: updatedViewModel.values.buyVehicleCost.numericValue,
    vacationWeeks: updatedViewModel.values.vacationWeeks.numericValue,
    fuelType: updatedViewModel.values.fuelType.value,
  }

  return values
}

interface CommuteByBikeFormViewModel {
  isValid: boolean
  values: CommuteByBikeValues
}

type CommuteByBikeValues = {
  currentDaysPerWeek: ComputeNumericValue,
  desiredDaysPerWeek: ComputeNumericValue,
  distanceToWork: ComputeNumericValue,
  kmPerLiter: ComputeNumericValue,
  buyVehicleCost: ComputeNumericValue,
  vacationWeeks: ComputeNumericValue,
  fuelType: ComputeGenericValue,
}

const getInitialCommuteByBikeFormViewModel = (improvementScoreItem: CommuteByBikeImprovementScoreItem): CommuteByBikeFormViewModel => {
  return {
    isValid: true,
    values: {
      currentDaysPerWeek: getInitialComputeNumericValue(improvementScoreItem.improvementScore.currentDaysPerWeek, "Aantal dagen per week met de auto (nu)"),
      desiredDaysPerWeek: getInitialComputeNumericValue(improvementScoreItem.improvementScore.desiredDaysPerWeek, "Gewenste aantal dagen per week met de auto"),
      distanceToWork: getInitialComputeNumericValue(improvementScoreItem.improvementScore.distanceToWork, "Afstand tot werk in kilometers"),
      kmPerLiter: getInitialComputeNumericValue(improvementScoreItem.improvementScore.kmPerLiter, "Aantal kilometers per liter"),
      buyVehicleCost: getInitialComputeNumericValue(improvementScoreItem.improvementScore.buyVehicleCost, "Kosten eventueel aan te schaffen fiets"),
      vacationWeeks: getInitialComputeNumericValue(improvementScoreItem.improvementScore.vacationWeeks, "Aantal weken vakantie per jaar"),
      fuelType: getInitialComputeGenericValue(improvementScoreItem.improvementScore.fuelType, "Type brandstof"),
    }
  }
}

const validateViewModel = (viewModel: CommuteByBikeFormViewModel): void => {
  viewModel.values.currentDaysPerWeek.validationResult = viewModel.values.currentDaysPerWeek.partialValidationResult.valid
    ? validateDaysPerWeek(viewModel.values.currentDaysPerWeek.numericValue, viewModel.values.desiredDaysPerWeek.numericValue, viewModel.values.currentDaysPerWeek.numericValue)
    : viewModel.values.currentDaysPerWeek.partialValidationResult

  viewModel.values.desiredDaysPerWeek.validationResult = viewModel.values.desiredDaysPerWeek.partialValidationResult.valid
    ? validateDaysPerWeek(viewModel.values.currentDaysPerWeek.numericValue, viewModel.values.desiredDaysPerWeek.numericValue, viewModel.values.desiredDaysPerWeek.numericValue)
    : viewModel.values.currentDaysPerWeek.partialValidationResult

  viewModel.values.distanceToWork.validationResult = viewModel.values.distanceToWork.partialValidationResult.valid
    ? validatePositiveNumber(viewModel.values.distanceToWork.numericValue)
    : viewModel.values.distanceToWork.partialValidationResult

  viewModel.values.kmPerLiter.validationResult = viewModel.values.kmPerLiter.partialValidationResult.valid
    ? validatePositiveNumber(viewModel.values.kmPerLiter.numericValue)
    : viewModel.values.kmPerLiter.partialValidationResult

  viewModel.values.vacationWeeks.validationResult = viewModel.values.vacationWeeks.partialValidationResult.valid
    ? validatePositiveNumber(viewModel.values.vacationWeeks.numericValue)
    : viewModel.values.vacationWeeks.partialValidationResult

  viewModel.values.buyVehicleCost.validationResult = viewModel.values.buyVehicleCost.partialValidationResult.valid
    ? validatePositiveNumber(viewModel.values.buyVehicleCost.numericValue)
    : viewModel.values.buyVehicleCost.partialValidationResult

  viewModel.isValid = Object.keys(viewModel.values).every((key: string) => viewModel.values[key as keyof CommuteByBikeValues].validationResult.valid)
}

const validateDaysPerWeek = (currentDaysPerWeek: number | undefined, desiredDaysPerWeek: number | undefined, fieldToCheck: number | undefined): ValidationResult => {
  if (fieldToCheck === undefined) return invalidValidationResult("Dit is een verplicht veld")
  if (fieldToCheck > 7) return invalidValidationResult("Vul maximaal 7 dagen in")
  if (fieldToCheck < 0) return invalidValidationResult("Vul minimaal 0 dagen in")
  if (currentDaysPerWeek !== undefined && desiredDaysPerWeek !== undefined && currentDaysPerWeek <= desiredDaysPerWeek) return invalidValidationResult("Voor een besparing dient u minder dan nu met de auto te rijden")
  return {valid: true}
}

